import { faSync } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState, useCallback } from 'react';

import Badge from '@/components/Badge';
import Button from '@/components/Button';
import Card from '@/components/Card';
import EmptyState from '@/components/EmptyState';
import IconicButton from '@/components/IconicButton';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Body } from '@/components/Typography';
import useMediaQuery from '@/hooks/useMediaQuery';
import { jobColors } from '@/styles/colors';
import CertificateRowItem from './CertificateRowItem';
import {
  fetchPendingCertificates,
  rejectCertificate,
  verifyCertificate,
  WorkerCertificatePhoto,
} from './api';

const HEADER_FIELDS = [
  'Worker',
  'Image',
  'Certificate',
  'Skill',
  'Expiration',
  'Submitted',
  'Status',
  'Actions',
];

const PendingCertificates = () => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [photos, setPhotos] = useState<WorkerCertificatePhoto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState<number | null>(null);
  const [isRejecting, setIsRejecting] = useState<number | null>(null);

  const loadCertificates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchPendingCertificates();
      if (response.success) {
        setPhotos(response.photos);
      } else {
        setError('Failed to load certificates');
      }
    } catch (err: any) {
      console.error('Error loading certificates:', err);
      setError(err.message || 'Failed to load certificates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  const handleVerify = async (id: number) => {
    setIsVerifying(id);
    try {
      await verifyCertificate(id);
      // Remove from list after successful verification
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (err: any) {
      console.error('Error verifying certificate:', err);
      throw err;
    } finally {
      setIsVerifying(null);
    }
  };

  const handleReject = async (id: number, comment?: string) => {
    setIsRejecting(id);
    try {
      await rejectCertificate(id, comment);
      // Remove from list after successful rejection
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (err: any) {
      console.error('Error rejecting certificate:', err);
      throw err;
    } finally {
      setIsRejecting(null);
    }
  };

  const handleActionComplete = () => {
    // Optionally refresh the list
    loadCertificates();
  };

  return (
    <Page
      headerPadding={!!phoneOnly}
      noPadding={!!phoneOnly}
      size={phoneOnly ? 'full' : 'xl'}
      title="Pending Certificates"
    >
      <Card noRadius={phoneOnly}>
        <Card.Section>
          <Stack justify="apart" align="center">
            <Stack gap={12} align="center">
              <Body weight="semibold">Pending Certificates</Body>
              {photos.length > 0 && (
                <Badge label={photos.length.toString()} status="warning" />
              )}
            </Stack>
            <IconicButton
              a11yLabel="Refresh list"
              appearance="plain"
              icon={faSync}
              onClick={loadCertificates}
              size="sm"
            />
          </Stack>
        </Card.Section>
        {isLoading ? (
          <LoadingState />
        ) : error ? (
          <Card.Section>
            <Stack vertical align="center" gap={16}>
              <Body color="danger">{error}</Body>
              <Button
                a11yLabel="Retry"
                label="Retry"
                onClick={loadCertificates}
              />
            </Stack>
          </Card.Section>
        ) : photos.length === 0 ? (
          <EmptyState
            title="No Pending Certificates"
            text="All certificate photo submissions have been processed."
          />
        ) : (
          <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
            <Table>
              {!phoneOnly && (
                <TableHeader
                  fields={HEADER_FIELDS}
                  headerBgColor={jobColors.headerLight}
                />
              )}
              <tbody>
                {photos.map((photo) => (
                  <CertificateRowItem
                    key={photo.id}
                    photo={photo}
                    onVerify={handleVerify}
                    onReject={handleReject}
                    isVerifying={isVerifying === photo.id}
                    isRejecting={isRejecting === photo.id}
                    onActionComplete={handleActionComplete}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </Page>
  );
};

export default PendingCertificates;
